import { useMyFinancialStatement } from "api/Client/balance";
import InputSearch, {
  DateInput,
  SelectInput,
} from "components/ClientLayoutPages/ClientInputField/InputsFilter";
import Container from "components/ClientLayoutPages/Container/Container";
import MyFinancialStatementModal from "components/Modal/Client/MyFinancialStatementModal/MyFinancialStatementModal";
import SkeletonMyOrder from "components/SkeletonList/SkeletonMyOrder";
import { useEffect, useState } from "react";
import {
  HiOutlineArrowLongLeft,
  HiOutlineArrowLongRight,
  HiOutlineBanknotes,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineHashtag,
  HiOutlineReceiptRefund,
  HiOutlineShoppingBag,
  HiOutlineWallet,
  HiOutlineXCircle,
} from "react-icons/hi2";

const getStateMeta = (state) => {
  if (state === "pending") {
    return { label: "انتظار", icon: <HiOutlineClock />, color: "text-amber-400" };
  }
  if (state === "approved") {
    return { label: "مكتملة", icon: <HiOutlineCheckCircle />, color: "text-emerald-400" };
  }
  return { label: "مرفوضة", icon: <HiOutlineXCircle />, color: "text-rose-400" };
};

const getTypeMeta = (type) => {
  switch (type) {
    case "bankNotice":
      return { label: "إشعار بنكي", icon: <HiOutlineBanknotes className="text-orange-400" /> };
    case "balanceRecharge":
      return { label: "شحن رصيد", icon: <HiOutlineWallet className="text-emerald-400" /> };
    case "transaction":
      return { label: "إرسال رصيد", icon: <HiOutlineReceiptRefund className="text-orange-300" /> };
    case "refund":
      return { label: "مرتجع", icon: <HiOutlineReceiptRefund className="text-amber-400" /> };
    case "purchaseInvoice":
      return { label: "فاتورة شراء", icon: <HiOutlineShoppingBag className="text-amber-400" /> };
    default:
      return { label: "- - -", icon: <HiOutlineReceiptRefund className="text-slate-400" /> };
  }
};

export default function FinancialStatement() {
  const [openMyFinancialStatementModal, setOpenMyFinancialStatementModal] =
    useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const {
    data: myFinancialStatement,
    isFetching: myFinancialStatementIsLoading,
    isSuccess: myFinancialStatementIsSuccess,
  } = useMyFinancialStatement(
    12,
    pageNumber,
    searchValue,
    status,
    fromDate,
    toDate
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageNumber]);

  return (
    <Container title="البيان المالي">
      <div className="portal-page-card mx-auto mb-8 mt-4 grid grid-cols-1 gap-4 rounded-[1.5rem] p-4 md:grid-cols-2 xl:grid-cols-4">
        <InputSearch
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setPageNumber={setPageNumber}
        />
        <SelectInput
          value={status}
          setValue={setStatus}
          setPageNumber={setPageNumber}
          options={[
            { value: "approved", label: "مكتمل" },
            { value: "rejected", label: "مرفوضة" },
            { value: "pending", label: "انتظار" },
          ]}
        />
        <DateInput
          title="من تاريخ"
          value={fromDate}
          setValue={setFromDate}
          setPageNumber={setPageNumber}
        />
        <DateInput
          title="إلى تاريخ"
          value={toDate}
          setValue={setToDate}
          setPageNumber={setPageNumber}
        />
      </div>

      {myFinancialStatementIsLoading ? <SkeletonMyOrder number={6} /> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {!myFinancialStatementIsLoading &&
          myFinancialStatementIsSuccess &&
          myFinancialStatement.data?.financial.length > 0 &&
          myFinancialStatement.data.financial.map((item) => {
            const stateMeta = getStateMeta(item.order_state);
            const typeMeta = getTypeMeta(item.order_type);

            return (
              <div
                key={item.id}
                onClick={() => {
                  setCurrentId(item.id);
                  setOpenMyFinancialStatementModal(true);
                }}
                className="portal-page-card card-animation grid cursor-pointer gap-2.5 rounded-[1.35rem] p-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold sm:text-sm ${stateMeta.color}`}
                  >
                    <span className="text-lg">{stateMeta.icon}</span>
                    {stateMeta.label}
                  </div>
                  <h2
                    className="text-lg font-bold portal-accent sm:text-xl"
                    style={{ direction: "ltr" }}
                  >
                    {Number(Math.abs(item.amount)).toLocaleString()}{" "}
                    <span className="text-sm">
                      {item.order_type === "bankNotice"
                        ? item.currency_bank
                        : item.currency_customer}
                    </span>
                  </h2>
                </div>

                <InfoRow icon={typeMeta.icon} label="نوع العملية" value={typeMeta.label} />
                <InfoRow
                  icon={<HiOutlineHashtag className="text-amber-400" />}
                  label="المعرّف"
                  value={item.id}
                />
                <InfoRow
                  icon={
                    item.order_type === "refund" ||
                    item.order_type === "purchaseInvoice" ? (
                      <HiOutlineShoppingBag className="text-amber-400" />
                    ) : (
                      <HiOutlineWallet className="text-orange-300" />
                    )
                  }
                  label="الوصف"
                  value={item.description}
                  clamp
                />
                <InfoRow
                  icon={<HiOutlineCalendarDays className="text-orange-400" />}
                  label="التاريخ"
                  value={item.created_at}
                  ltr
                />
                <InfoRow
                  icon={<HiOutlineBanknotes className="text-emerald-400" />}
                  label="الرصيد قبل العملية"
                  value={`${Number(item.total_before_operation).toLocaleString()} ${item.currency_customer}`}
                  ltr
                />
              </div>
            );
          })}
      </div>

      <Pagination
        current={myFinancialStatement?.data?.pagination?.current_page || 0}
        total={myFinancialStatement?.data?.pagination?.last_page || 0}
        onPrev={() => setPageNumber((prev) => prev - 1)}
        onNext={() => setPageNumber((prev) => prev + 1)}
        disablePrev={pageNumber === 1 || myFinancialStatementIsLoading}
        disableNext={
          myFinancialStatement?.data?.financial.length === 0 ||
          pageNumber >= myFinancialStatement?.data.pagination?.last_page ||
          myFinancialStatement?.data?.pagination?.total === 0 ||
          myFinancialStatementIsLoading
        }
      />

      {openMyFinancialStatementModal ? (
        <MyFinancialStatementModal
          isOpen={openMyFinancialStatementModal}
          toggle={setOpenMyFinancialStatementModal}
          financialId={currentId}
        />
      ) : null}
    </Container>
  );
}

function InfoRow({ icon, label, value, clamp = false, ltr = false }) {
  return (
    <div
      className="flex items-start gap-2.5"
      style={{ color: "var(--layout-text-muted)" }}
    >
      <div className="text-lg">{icon}</div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-semibold portal-accent">{label}</span>
        <span
          className={`text-sm font-bold sm:text-[0.95rem] ${clamp ? "line-clamp-2" : ""}`}
          style={{ direction: ltr ? "ltr" : "rtl", color: "var(--layout-text)" }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function Pagination({ current, total, onPrev, onNext, disablePrev, disableNext }) {
  return (
    <div className="mt-16 flex w-full flex-col items-center">
      <span className="flex gap-1 text-sm portal-accent">
        <span className="font-semibold">{current}</span>
        <span className="font-medium">من أصل</span>
        <span className="font-semibold">{total}</span>
      </span>
      <div className="mt-3 flex gap-4">
        <button
          onClick={onPrev}
          disabled={disablePrev}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          <HiOutlineArrowLongRight />
          السابق
        </button>
        <button
          onClick={onNext}
          disabled={disableNext}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          التالي
          <HiOutlineArrowLongLeft />
        </button>
      </div>
    </div>
  );
}
